import React, { useEffect, useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  FaArrowLeft,
  FaArrowRight,
  FaCheck,
  FaExclamationTriangle,
  FaSearch,
  FaShieldAlt,
  FaTimes,
} from "react-icons/fa";
import {
  apiRbacGetRoleMatrix,
  apiRbacGetUserDetails,
} from "../api/index";
import "./StaffFeatureAccessEditor.css";

const DOMAIN_LABELS = {
  orders: "Orders & Cart",
  vendors: "Vendor Onboarding",
  shop: "Shop & Listings",
  restaurants: "Restaurants",
  retail_wholesale: "Retail & Wholesale",
  healthcare: "Healthcare",
  brands: "Brands",
  three_pl: "3PL Partners",
  users: "Users & Staff",
  riders: "Riders",
  delivery: "Delivery Fees & Zones",
  wallet: "Wallet & Media Billing",
  refunds: "Refunds",
  categorization: "Categories & Units",
  content: "Content & Knowledge Base",
  advertising: "Advertising",
  notifications: "Notifications & SMS",
  messaging: "WhatsApp & Messaging",
  analytics: "Dashboard & Analytics",
  compliance: "Audit, Seed Ops & Compliance",
  kitchen: "Kitchen Display & Stations",
  ticketing: "Events & Ticketing",
  access_control: "Access & Country Control",
  settings: "Settings & POS",
  system: "System Administration",
};

const ACTION_LABELS = {
  read: "View",
  create: "Create",
  update: "Edit",
  delete: "Delete",
  manage: "Manage",
  export: "Export",
  approve: "Approve",
  assign: "Assign",
};

const applyOverrides = (defaults, overrides) => {
  const effective = new Set(defaults || []);
  (overrides || []).forEach((override) => {
    if (override.type === "DENY") effective.delete(override.permissionKey);
    else effective.add(override.permissionKey);
  });
  return effective;
};

const StaffFeatureAccessEditor = ({
  role,
  userId,
  canCustomize,
  onChange,
  onReadyChange,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [permissions, setPermissions] = useState([]);
  const [roleMatrix, setRoleMatrix] = useState({});
  const [savedOverrides, setSavedOverrides] = useState([]);
  const [savedRole, setSavedRole] = useState("");
  const [effectiveKeys, setEffectiveKeys] = useState(new Set());
  const [availableSearch, setAvailableSearch] = useState("");
  const [assignedSearch, setAssignedSearch] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    onReadyChange?.(false);

    Promise.all([
      apiRbacGetRoleMatrix(),
      userId ? apiRbacGetUserDetails(userId) : Promise.resolve(null),
    ])
      .then(([matrixResponse, detailsResponse]) => {
        if (!active) return;
        const matrixData = matrixResponse.data.data || {};
        const details = detailsResponse?.data?.data || null;
        setPermissions(matrixData.permissions || []);
        setRoleMatrix(matrixData.matrix || {});
        setSavedOverrides(details?.permissionOverrides || []);
        setSavedRole(details?.user?.user_role || "");
        onReadyChange?.(true);
      })
      .catch((requestError) => {
        if (!active) return;
        setError(
          requestError?.response?.data?.message ||
            "Unable to load the permission catalogue."
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [onReadyChange, userId]);

  const baseKeys = useMemo(() => {
    const roleData = roleMatrix?.[role];
    return new Set(roleData?.permissions || []);
  }, [role, roleMatrix]);

  useEffect(() => {
    if (loading || !role) return;
    if (role === "SUPERUSER") {
      setEffectiveKeys(new Set(permissions.map((permission) => permission.key)));
      onChange?.([]);
      return;
    }
    const relevantOverrides =
      !userId || !savedRole || savedRole === role ? savedOverrides : [];
    const next = applyOverrides(baseKeys, relevantOverrides);
    setEffectiveKeys(next);
  }, [
    baseKeys,
    loading,
    permissions,
    role,
    savedOverrides,
    savedRole,
    userId,
  ]);

  const overrides = useMemo(() => {
    if (!role || role === "SUPERUSER") return [];
    return permissions.reduce((result, permission) => {
      const inherited = baseKeys.has(permission.key);
      const desired = effectiveKeys.has(permission.key);
      if (inherited === desired) return result;
      result.push({
        permissionKey: permission.key,
        type: desired ? "GRANT" : "DENY",
      });
      return result;
    }, []);
  }, [baseKeys, effectiveKeys, permissions, role]);

  useEffect(() => {
    if (!loading) onChange?.(overrides);
  }, [loading, onChange, overrides]);

  const domains = useMemo(() => {
    const grouped = permissions.reduce((result, permission) => {
      if (!result[permission.domain]) result[permission.domain] = [];
      result[permission.domain].push(permission);
      return result;
    }, {});

    return Object.entries(grouped)
      .map(([domain, domainPermissions]) => {
        const activePermissions = domainPermissions.filter((permission) =>
          effectiveKeys.has(permission.key)
        );
        const inheritedCount = activePermissions.filter((permission) =>
          baseKeys.has(permission.key)
        ).length;
        return {
          domain,
          label: DOMAIN_LABELS[domain] || domain,
          permissions: domainPermissions,
          activePermissions,
          inheritedCount,
          customCount: activePermissions.length - inheritedCount,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [baseKeys, effectiveKeys, permissions]);

  const availableDomains = domains.filter(
    (domain) =>
      domain.activePermissions.length === 0 &&
      domain.label.toLowerCase().includes(availableSearch.toLowerCase())
  );
  const assignedDomains = domains.filter(
    (domain) =>
      domain.activePermissions.length > 0 &&
      domain.label.toLowerCase().includes(assignedSearch.toLowerCase())
  );

  const addDomain = (domain) => {
    if (!canCustomize || role === "SUPERUSER") return;
    const readPermission =
      domain.permissions.find((permission) => permission.action === "read") ||
      domain.permissions[0];
    if (!readPermission) return;
    setEffectiveKeys((current) => new Set([...current, readPermission.key]));
  };

  const removeDomain = (domain) => {
    if (!canCustomize || role === "SUPERUSER") return;
    setEffectiveKeys((current) => {
      const next = new Set(current);
      domain.permissions.forEach((permission) => next.delete(permission.key));
      return next;
    });
  };

  const togglePermission = (permission) => {
    if (!canCustomize || role === "SUPERUSER") return;
    setEffectiveKeys((current) => {
      const next = new Set(current);
      if (next.has(permission.key)) next.delete(permission.key);
      else next.add(permission.key);
      return next;
    });
  };

  if (!role) {
    return (
      <section className="staff-access-editor is-empty">
        <FaShieldAlt aria-hidden="true" />
        <strong>Select a role to configure feature access</strong>
        <span>The role establishes the account's baseline permissions.</span>
      </section>
    );
  }

  if (loading) {
    return (
      <section className="staff-access-editor is-loading">
        <Spinner animation="border" />
        <strong>Loading feature access</strong>
      </section>
    );
  }

  if (error) {
    return (
      <section className="staff-access-editor is-error">
        <FaExclamationTriangle aria-hidden="true" />
        <span>{error}</span>
      </section>
    );
  }

  return (
    <section className="staff-access-editor">
      <div className="staff-access-editor__heading">
        <div>
          <span>Feature access</span>
          <h3>Assign operational features</h3>
          <p>
            The {role.toLowerCase()} role supplies the baseline. Changes here
            are saved as explicit access exceptions.
          </p>
        </div>
        <div className="staff-access-editor__summary">
          <strong>{assignedDomains.length}</strong>
          <span>features assigned</span>
          <small>{overrides.length} exceptions</small>
        </div>
      </div>

      {!canCustomize && (
        <div className="staff-access-editor__notice">
          <FaShieldAlt aria-hidden="true" />
          Only a Super User can customize access beyond the selected role.
        </div>
      )}

      {role === "SUPERUSER" && (
        <div className="staff-access-editor__notice is-warning">
          <FaExclamationTriangle aria-hidden="true" />
          Super Users always have unrestricted platform access.
        </div>
      )}

      <div className="staff-access-editor__panes">
        <div className="staff-access-pane">
          <div className="staff-access-pane__heading">
            <div>
              <span>Available features</span>
              <strong>{availableDomains.length}</strong>
            </div>
            <label>
              <FaSearch aria-hidden="true" />
              <input
                type="search"
                value={availableSearch}
                onChange={(event) => setAvailableSearch(event.target.value)}
                placeholder="Search available"
              />
              {availableSearch && (
                <button
                  type="button"
                  onClick={() => setAvailableSearch("")}
                  aria-label="Clear available-feature search"
                >
                  <FaTimes aria-hidden="true" />
                </button>
              )}
            </label>
          </div>
          <div className="staff-access-pane__list">
            {availableDomains.length ? (
              availableDomains.map((domain) => (
                <button
                  type="button"
                  className="staff-access-available"
                  key={domain.domain}
                  onClick={() => addDomain(domain)}
                  disabled={!canCustomize || role === "SUPERUSER"}
                >
                  <span>
                    <strong>{domain.label}</strong>
                    <small>{domain.permissions.length} available actions</small>
                  </span>
                  <FaArrowRight aria-hidden="true" />
                </button>
              ))
            ) : (
              <div className="staff-access-pane__empty">
                <FaCheck aria-hidden="true" />
                <span>No available features match this search.</span>
              </div>
            )}
          </div>
        </div>

        <div className="staff-access-pane is-assigned">
          <div className="staff-access-pane__heading">
            <div>
              <span>Assigned features</span>
              <strong>{assignedDomains.length}</strong>
            </div>
            <label>
              <FaSearch aria-hidden="true" />
              <input
                type="search"
                value={assignedSearch}
                onChange={(event) => setAssignedSearch(event.target.value)}
                placeholder="Search assigned"
              />
              {assignedSearch && (
                <button
                  type="button"
                  onClick={() => setAssignedSearch("")}
                  aria-label="Clear assigned-feature search"
                >
                  <FaTimes aria-hidden="true" />
                </button>
              )}
            </label>
          </div>
          <div className="staff-access-pane__list">
            {assignedDomains.length ? (
              assignedDomains.map((domain) => (
                <article className="staff-access-assigned" key={domain.domain}>
                  <div className="staff-access-assigned__heading">
                    <button
                      type="button"
                      onClick={() => removeDomain(domain)}
                      disabled={!canCustomize || role === "SUPERUSER"}
                      aria-label={`Remove ${domain.label} access`}
                      title={`Remove ${domain.label} access`}
                    >
                      <FaArrowLeft aria-hidden="true" />
                    </button>
                    <div>
                      <strong>{domain.label}</strong>
                      <span>
                        {domain.inheritedCount} inherited
                        {domain.customCount
                          ? ` · ${domain.customCount} custom`
                          : ""}
                      </span>
                    </div>
                  </div>
                  <div className="staff-access-actions">
                    {domain.permissions.map((permission) => {
                      const enabled = effectiveKeys.has(permission.key);
                      const inherited = baseKeys.has(permission.key);
                      return (
                        <button
                          type="button"
                          className={`${enabled ? "is-enabled" : ""}${
                            inherited ? " is-inherited" : ""
                          }`}
                          key={permission.key}
                          title={permission.description}
                          aria-pressed={enabled}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            togglePermission(permission);
                          }}
                          disabled={!canCustomize || role === "SUPERUSER"}
                        >
                          <span>
                            {ACTION_LABELS[permission.action] ||
                              permission.action}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </article>
              ))
            ) : (
              <div className="staff-access-pane__empty">
                <FaShieldAlt aria-hidden="true" />
                <span>No features are currently assigned.</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default StaffFeatureAccessEditor;
